export type WindowEndpoint = "start" | "end";

const endpointStableFrameCount = 3;

interface WindowEndpointRetentionOptions {
    requestFrame: (callback: FrameRequestCallback) => number;
    cancelFrame: (frameId: number) => void;
    getScrollY: () => number;
    getScrollHeight: () => number;
    getViewportHeight: () => number;
    scrollTo: (scrollY: number) => void;
}

export function isEditableWindowEndpointTarget(target: EventTarget | null): boolean {
    return (
        target instanceof HTMLInputElement ||
        target instanceof HTMLTextAreaElement ||
        target instanceof HTMLSelectElement ||
        (target instanceof HTMLElement && target.isContentEditable)
    );
}

export function createWindowEndpointRetention(options: WindowEndpointRetentionOptions) {
    let requestedEndpoint: WindowEndpoint | undefined;
    let pendingFrame: number | undefined;
    let stableFrameCount = 0;

    const cancel = () => {
        requestedEndpoint = undefined;
        stableFrameCount = 0;
    };
    const handleKey = (key: string, excludedTarget: boolean) => {
        if (excludedTarget) {
            cancel();
        } else if (key === "Home") {
            requestedEndpoint = "start";
            stableFrameCount = 0;
        } else if (key === "End") {
            requestedEndpoint = "end";
            stableFrameCount = 0;
        } else {
            cancel();
        }
    };
    const correct = () => {
        if (pendingFrame !== undefined) return;
        pendingFrame = options.requestFrame(() => {
            pendingFrame = undefined;
            if (!requestedEndpoint) return;
            const targetScrollY =
                requestedEndpoint === "start"
                    ? 0
                    : Math.max(0, options.getScrollHeight() - options.getViewportHeight());
            if (Math.abs(options.getScrollY() - targetScrollY) > 1) {
                stableFrameCount = 0;
                options.scrollTo(targetScrollY);
            } else {
                stableFrameCount++;
            }
            if (stableFrameCount < endpointStableFrameCount) {
                correct();
            }
        });
    };
    const dispose = () => {
        if (pendingFrame !== undefined) {
            options.cancelFrame(pendingFrame);
            pendingFrame = undefined;
        }
    };

    return {cancel, correct, dispose, handleKey};
}
